import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { pushEvent } from '../../utils/analytics';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import PrerenderReady from '../../components/PrerenderReady';
import useABTest from '../../hooks/useABTest';
import { useThemeMode } from '../../design-system/theme';
import { ACTIVE_EXPERIMENTS } from '../../utils/experiments';
import {
  AI_TRADING_CHALLENGE_NAME,
  buildChallengeCalendarLinks,
  normalizeChallengeSlug,
  formatChallengeDateTime,
  pickKickoffAndDemo,
  selectCurrentChallengeRun,
  toChallengeSlug,
  youtubeLiveUrl,
} from './challengePageUtils';
import TrustBar from '../../components/TrustBar/TrustBar';
import botspotLogo from '../../assets/botspot_logo_horizontal.png';
import './ChallengePageClarity.css';
import './ChallengeBookingPage.css';
const { getNoindexRoute } = require('../../site/publicPageSeo');
const { SITE_BASE_URL } = require('../../site/siteRoutes');
const { challengeEventStructuredData } = require('../../site/structuredData');

// Rob, 2026-10-03: setup calls are booked on the BotSpot team event. Node's
// webhook and backfill still recognize the old Lumiwealth event
// (lumiwealth/botspot-call) for bookings made before the switch.
const CALENDLY_URL =
  'https://calendly.com/botspot-trade/setup-call?hide_event_type_details=1&hide_gdpr_banner=1';

// Rob, 2026-09-11: this page lives at /challenges/welcome with no run in the
// URL. The registration that just happened (navigation state, then session
// storage) names the run. With no context at all, the current public run is
// shown, still straight from the catalog.
// Rob, 2026-10-01: the embed opened on months with no open times. Node reads
// the first real open slot from Calendly (/calendly/availability) and the
// embed opens on that month, in the visitor's own time zone. If Node cannot
// say, Calendly keeps its own default month. Nothing here is a guessed date.
const AVAILABILITY_WAIT_MS = 3000;
const monthOf = (iso) => {
  const date = new Date(iso);
  if (!iso || !Number.isFinite(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};
const calendlyUrlFor = (month) => {
  const url = new URL(CALENDLY_URL);
  if (month) url.searchParams.set('month', month);
  return url.toString();
};

const readBookingContext = () => {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem('challengeBookingContext') || '{}');
    return saved && typeof saved === 'object' ? saved : {};
  } catch {
    return {};
  }
};

export default function ChallengeBookingPage() {
  const navigate = useNavigate();
  const [theme] = useThemeMode();
  const location = useLocation();
  const widgetRef = useRef(null);
  const context = useMemo(
    () => location.state?.bookingContext || readBookingContext(),
    [location.state],
  );
  const slug = context.slug || null;
  // Campaign label for Calendly and the Bootcamp link. Never a route.
  const campaignSlug = slug || toChallengeSlug(AI_TRADING_CHALLENGE_NAME);
  const completedEvents = useRef(new Set());
  const [widgetError, setWidgetError] = useState(false);
  const [widgetReady, setWidgetReady] = useState(false);
  // undefined while Node is asked, then a YYYY-MM month or null.
  const [openMonth, setOpenMonth] = useState(undefined);
  const [challenge, setChallenge] = useState(null);
  const [scheduleError, setScheduleError] = useState(false);
  // Firefox can finish a summary click and still leave the disclosure closed,
  // and a later render used to drop a native toggle. React owns `open`.
  const [detailsOpen, setDetailsOpen] = useState(location.hash === '#event-details');
  useEffect(() => {
    if (location.hash === '#event-details') {
      setDetailsOpen(true);
    }
  }, [location.hash]);

  const bookingExperiment = ACTIVE_EXPERIMENTS.CHALLENGE_BOOKING_SETUP_VS_PROJECT;
  const { isTest: isProjectVariant } = useABTest(
    bookingExperiment.name,
    bookingExperiment.featureFlag,
    'control',
    {
      page: 'challenge_booking',
      registration_id: context.registrationId || undefined,
      validVariants: ['control', 'test'],
      normalizeControlExposure: true,
    },
  );

  useEffect(() => {
    let mounted = true;
    const apiUrl = getApiBaseUrl() || 'https://api.botspot.trade';
    const abort = new AbortController();
    const deadline = window.setTimeout(() => {
      abort.abort();
      if (mounted) setScheduleError(true);
    }, 20000);

    const readGroups = (listPayload) =>
      Array.isArray(listPayload)
        ? listPayload
        : listPayload.sessionGroups || listPayload.data || [];

    const findRegisteredRun = async () => {
      let match;
      let page = 1;
      let pages = 1;
      do {
        const listResponse = await fetch(
          `${apiUrl}/courses/session-groups?active=false&registrationAccess=public&limit=100&page=${page}`,
          { signal: abort.signal },
        );
        if (!listResponse.ok) throw new Error(`session_groups_${listResponse.status}`);
        const listPayload = await listResponse.json();
        // A registration's details must never roll forward to a different run.
        match = readGroups(listPayload).find(
          (group) => normalizeChallengeSlug(group.name) === normalizeChallengeSlug(slug),
        );
        pages = Number(listPayload.pagination?.pages) || 1;
        page += 1;
      } while (!match && page <= pages && !abort.signal.aborted);
      return match;
    };

    const findCurrentRun = async () => {
      const listResponse = await fetch(
        `${apiUrl}/courses/session-groups?active=true&registrationAccess=public&limit=50`,
        { signal: abort.signal },
      );
      if (!listResponse.ok) throw new Error(`session_groups_${listResponse.status}`);
      return selectCurrentChallengeRun(readGroups(await listResponse.json()));
    };

    (async () => {
      try {
        const match = slug ? await findRegisteredRun() : await findCurrentRun();
        if (!match) throw new Error('challenge_not_found');

        const detailResponse = await fetch(`${apiUrl}/courses/session-groups/${match.id}`, {
          signal: abort.signal,
        });
        if (!detailResponse.ok) throw new Error('challenge_details_unavailable');
        const detail = await detailResponse.json();
        if (slug && normalizeChallengeSlug(detail.name) !== normalizeChallengeSlug(slug))
          throw new Error('challenge_identity_mismatch');
        if (!mounted) return;
        setChallenge({ ...match, ...detail });
        setScheduleError(false);
      } catch {
        if (!mounted) return;
        setScheduleError(true);
      } finally {
        window.clearTimeout(deadline);
      }
    })();

    return () => {
      mounted = false;
      abort.abort();
      window.clearTimeout(deadline);
    };
  }, [slug]);

  useEffect(() => {
    let active = true;
    const abort = new AbortController();
    const deadline = window.setTimeout(() => abort.abort(), AVAILABILITY_WAIT_MS);
    (async () => {
      let month = null;
      try {
        const response = await fetch(
          `${getApiBaseUrl() || 'https://api.botspot.trade'}/calendly/availability`,
          { signal: abort.signal },
        );
        if (response.ok) {
          const result = await response.json();
          if (result?.status === 'available') month = monthOf(result.firstAvailableAt);
        }
      } catch {
        month = null;
      } finally {
        window.clearTimeout(deadline);
      }
      if (active) setOpenMonth(month);
    })();
    return () => {
      active = false;
      abort.abort();
      window.clearTimeout(deadline);
    };
  }, []);
  const calendlyUrl = useMemo(
    () => (openMonth === undefined ? null : calendlyUrlFor(openMonth)),
    [openMonth],
  );

  useEffect(() => {
    pushEvent({
      event: 'challenge_booking_page_viewed',
      source: window.location.pathname,
      registration_id: context.registrationId || undefined,
    });
  }, [context.registrationId]);

  useEffect(() => {
    // Wait for the open month so the embed is built once, on the right month.
    if (!calendlyUrl) return undefined;
    const mount = widgetRef.current;
    let active = true;
    let initialized = false;
    const deadline = window.setTimeout(() => {
      if (active) setWidgetError(true);
    }, 15000);

    const initialize = () => {
      if (!active || initialized || !window.Calendly || !widgetRef.current) return;
      initialized = true;
      try {
        window.Calendly.initInlineWidget({
          url: calendlyUrl,
          parentElement: mount,
          prefill: {
            email: context.email || '',
            name: context.name || '',
          },
          utm: {
            utmSource: 'ai_trading_challenge',
            utmMedium: 'post_registration',
            utmCampaign: campaignSlug,
            utmContent: context.registrationId || '',
            utmTerm: context.bookingReference?.attemptId || '',
          },
        });
      } catch {
        setWidgetError(true);
        return;
      }
      pushEvent({
        event: 'calendly_widget_loaded',
        source: window.location.pathname,
        widget_url: calendlyUrl,
      });
    };

    const failed = () => {
      if (active) setWidgetError(true);
    };
    let script;
    const existingScript = document.querySelector('script[data-challenge-calendly="true"]');
    if (existingScript) {
      script = existingScript;
      initialize();
      existingScript.addEventListener('load', initialize);
    } else {
      script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.dataset.challengeCalendly = 'true';
      script.addEventListener('load', initialize);
      document.head.appendChild(script);
    }

    const handleCalendlyMessage = (event) => {
      if (event.origin !== 'https://calendly.com') return;
      if (
        !event.source ||
        !Array.from(mount?.querySelectorAll('iframe') || []).some(
          (frame) => frame.contentWindow === event.source,
        )
      )
        return;
      if (
        [
          'calendly.profile_page_viewed',
          'calendly.event_type_viewed',
          'calendly.date_and_time_selected',
          'calendly.event_scheduled',
        ].includes(event.data?.event)
      ) {
        window.clearTimeout(deadline);
        setWidgetReady(true);
        setWidgetError(false);
      }
      if (event.data?.event !== 'calendly.event_scheduled') return;
      const inviteeUri = event.data?.payload?.invitee?.uri || null;
      const eventUri = event.data?.payload?.event?.uri || null;
      const inviteeId = inviteeUri?.split('/').filter(Boolean).pop() || null;
      const eventId = inviteeId ? `calendly-${inviteeId}` : null;
      if (!/^https:\/\/api\.calendly\.com\/scheduled_events\/[a-zA-Z0-9-]+$/.test(eventUri || ''))
        return;
      if (
        inviteeUri !== `${eventUri}/invitees/${inviteeId}` ||
        !/^[a-zA-Z0-9-]+$/.test(inviteeId || '')
      )
        return;
      if (completedEvents.current.has(eventId)) return;
      completedEvents.current.add(eventId);
      try {
        window.sessionStorage.setItem(
          'challengeCallContext',
          JSON.stringify({
            inviteeUri,
            eventUri,
            slug: campaignSlug,
            readToken: context.bookingReference?.readToken || null,
          }),
        );
      } catch {
        /* Storage denial must not prevent the booking handoff. */
      }
      // The verified Calendly webhook owns the single BookedCall conversion.
      // This browser message only advances the customer to the confirmation page.
      navigate('/challenges/call-confirmed', {
        state: {
          callContext: {
            slug: campaignSlug,
            inviteeUri,
            eventUri,
            readToken: context.bookingReference?.readToken || null,
          },
        },
      });
    };

    script.addEventListener('error', failed);
    window.addEventListener('message', handleCalendlyMessage);
    initialize();

    return () => {
      active = false;
      window.clearTimeout(deadline);
      window.removeEventListener('message', handleCalendlyMessage);
      script.removeEventListener('load', initialize);
      script.removeEventListener('error', failed);
      mount?.replaceChildren();
    };
  }, [
    context.email,
    context.name,
    context.registrationId,
    context.bookingReference,
    navigate,
    campaignSlug,
    calendlyUrl,
  ]);

  const { kickoff, demoDay } = useMemo(
    () => pickKickoffAndDemo(challenge?.sessions || []),
    [challenge],
  );
  const apiUrl = getApiBaseUrl() || 'https://api.botspot.trade';

  const sessions = [kickoff, demoDay].filter(Boolean);
  // The call ad lands here, so the crawler document (a build-time snapshot of
  // this render) must show the next kickoff and a way to get a free seat. It
  // is settled once the schedule loaded; a failed read is never published.
  const upcomingKickoffAt =
    kickoff?.scheduledAt && new Date(kickoff.scheduledAt).getTime() > Date.now()
      ? kickoff.scheduledAt
      : null;
  const prerenderReady = Boolean(challenge) && !scheduleError;
  const seo = getNoindexRoute('/challenges/welcome');
  // The free kickoff as Event schema, pointing at the registration page, so
  // the ad crawler can verify the date this page shows. Loaded data only.
  const eventSchema =
    prerenderReady && upcomingKickoffAt
      ? challengeEventStructuredData({
          path: '/challenges',
          name: String(challenge?.course?.title || challenge?.name || AI_TRADING_CHALLENGE_NAME),
          description: seo?.description,
          startDate: upcomingKickoffAt,
          isFree: true,
        })
      : null;
  const callHeadline = isProjectVariant
    ? 'Plan your first AI trading project.'
    : 'Get your trading setup ready.';
  const callCta = isProjectVariant ? 'Plan my project with the team' : 'Book my free setup call';
  const fallbackUrl = new URL(calendlyUrl || CALENDLY_URL);
  fallbackUrl.searchParams.set('utm_source', 'ai_trading_challenge');
  fallbackUrl.searchParams.set('utm_medium', 'post_registration');
  fallbackUrl.searchParams.set('utm_campaign', campaignSlug);
  if (context.registrationId) fallbackUrl.searchParams.set('utm_content', context.registrationId);
  if (context.bookingReference?.attemptId)
    fallbackUrl.searchParams.set('utm_term', context.bookingReference.attemptId);

  // Rob 2026-10-03: after the two questions people go straight to booking a call. Nothing goes above the calendar without Rob's explicit OK.
  return (
    <main
      className="challenge-next challenge-next--welcome challenge-clarity-review"
      data-testid="challenge-booking-page"
      data-education-design-variant="clarity"
      data-education-theme={theme}
      data-experiment-active="false"
    >
      <PrerenderReady
        ready={prerenderReady}
        state={upcomingKickoffAt ? 'scheduled' : 'unscheduled'}
      />
      <Helmet>
        <title>Your AI Trading Challenge | BotSpot</title>
        <meta name="robots" content="noindex" />
        {seo?.description ? <meta name="description" content={seo.description} /> : null}
        <link rel="canonical" href={`${SITE_BASE_URL}/challenges/welcome`} />
        {eventSchema ? (
          <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
        ) : null}
      </Helmet>
      {/* The site navbar is hidden on this funnel step (App.tsx), so the page
          carries its own small brand mark. */}
      <div className="challenge-next__brand">
        <img src={botspotLogo} alt="BotSpot by Lumiwealth" width="1384" height="430" />
      </div>
      <p className="challenge-next__registration">
        {context.registrationId ? 'Your challenge seat is saved' : 'Free AI Trading Challenge'}
      </p>
      {/* Rob, 2026-10-03: this page sends a new registrant to book a call. The
          booking heading and calendar follow the confirmation line directly.
          No show strip, countdown or trade question goes above them; the
          calendar links and Zoom live in the "Live session details" below. */}
      <div className="challenge-next__booking-layout" data-calendar-error={widgetError}>
        <section className="challenge-next__hero">
          <div className="challenge-next__intro">
            <div className="challenge-next__visual-heading">
              <h1>{callHeadline}</h1>
            </div>
            <p className="challenge-next__call-benefit">
              Work through your trading idea, tools, and setup questions.
            </p>
            <p className="challenge-next__call-facts">
              <strong>Free 30-minute call</strong>
              <span>With the challenge team</span>
            </p>
            <div className="challenge-next__actions">
              <a
                className="challenge-next__booking-link"
                href={widgetError ? fallbackUrl.toString() : '#book-call'}
                target={widgetError ? '_blank' : undefined}
                rel={widgetError ? 'noopener noreferrer' : undefined}
                onClick={() =>
                  pushEvent({
                    event: 'challenge_setup_call_clicked',
                    source: window.location.pathname,
                    registration_id: context.registrationId || undefined,
                    variant: isProjectVariant ? 'test' : 'control',
                  })
                }
              >
                {callCta}
              </a>
            </div>
            <p className="challenge-next__expectations">Optional. No purchase required.</p>
            {!context.registrationId && upcomingKickoffAt ? (
              <p className="challenge-next__free-seat" data-testid="challenge-free-seat">
                Not registered yet? <Link to="/challenges">Save your free seat</Link> for the live
                kickoff on{' '}
                <time dateTime={upcomingKickoffAt}>
                  {formatChallengeDateTime(upcomingKickoffAt)}
                </time>
                .
              </p>
            ) : null}
          </div>
        </section>
        <div className="challenge-next__booking-tool">
          <section
            id="book-call"
            className="challenge-next__calendar"
            data-ready={widgetReady}
            data-error={widgetError}
            aria-busy={!widgetReady && !widgetError}
            aria-label="Book a free call with the challenge team"
          >
            <div
              ref={widgetRef}
              className="challenge-next__calendly-mount"
              data-testid="challenge-calendly"
            />
            {!widgetReady && (
              <div className="challenge-next__calendar-status" role="status">
                <strong>
                  {widgetError ? 'Calendar unavailable here' : 'Finding available times…'}
                </strong>
                <p>
                  {widgetError
                    ? 'Use the booking button to choose a time on Calendly. Your workshop registration is unaffected.'
                    : 'Available times will appear here.'}
                </p>
              </div>
            )}
          </section>
          {/* Rob, 2026-10-20: one short line under the calendar (never above
              it) so a new signup finds our email and replies "in", which
              keeps the reminders out of spam. No text-reply ask: replies to
              our SMS number are not handled as confirmations. */}
          {context.registrationId ? (
            <p className="challenge-next__call-disclosure" data-testid="challenge-inbox-reply">
              Check your inbox. If our email is in spam or Promotions, move it to your inbox and
              reply &quot;in&quot;.
            </p>
          ) : null}
          <p className="challenge-next__call-disclosure">
            We can also discuss whether the paid Bootcamp fits your goals.
          </p>
          {!widgetError && (
            <p className="challenge-next__scheduler-fallback">
              Prefer a separate window?{' '}
              <a href={fallbackUrl.toString()} target="_blank" rel="noopener noreferrer">
                Open in a separate window
              </a>
            </p>
          )}
        </div>
      </div>
      <details
        id="event-details"
        className="challenge-next__block challenge-next__session-details"
        open={detailsOpen}
      >
        <summary
          onClick={(event) => {
            event.preventDefault();
            setDetailsOpen((open) => !open);
          }}
        >
          <span>
            Live session details
            <small>Your calendar and joining links</small>
          </span>
        </summary>
        {sessions.length ? (
          <ul className="challenge-next__sessions">
            {sessions.map((session, index) => {
              const links = buildChallengeCalendarLinks(session, apiUrl);
              const youtubeUrl = youtubeLiveUrl(session);
              return (
                <li key={session.id}>
                  <div className="challenge-next__session-title">
                    <strong>{session.title}</strong>
                    <time dateTime={session.scheduledAt}>
                      {formatChallengeDateTime(session.scheduledAt)}
                    </time>
                  </div>
                  <div className="challenge-next__session-actions">
                    {links ? (
                      <>
                        <a
                          className="challenge-next__action challenge-next__action--primary"
                          href={links.google}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Google Calendar
                        </a>
                        <a className="challenge-next__action" href={links.ics} download>
                          Apple or Outlook
                        </a>
                      </>
                    ) : null}
                    {session.joinUrl ? (
                      <a
                        className="challenge-next__action"
                        href={session.joinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Join on Zoom
                      </a>
                    ) : null}
                    {youtubeUrl ? (
                      <a
                        className="challenge-next__action"
                        href={youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Watch on YouTube
                      </a>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="challenge-next__status">
            {scheduleError
              ? 'The live schedule is unavailable here. Check your confirmation email for your joining details.'
              : 'Loading your live schedule...'}
          </p>
        )}
      </details>
      <TrustBar />
      <p className="challenge-next__bootcamp-footer">
        Prefer to explore on your own?{' '}
        <Link
          to={`/courses/ai-trading-bootcamp?utm_source=ai_trading_challenge&utm_medium=post_registration_footer&utm_campaign=${campaignSlug}`}
        >
          View the Bootcamp
        </Link>
      </p>
      <Link className="challenge-next__skip" to="/challenges">
        Back to the challenge
      </Link>
    </main>
  );
}
